import { RotateCcw, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useActionsParametres, useParametres } from '@/api/hooks';
import { LIBELLE_GROUPE_PARAMETRE, LIBELLE_PRIORITE, type DelaisCourrier, type GroupeParametre, type ParametreApp, type Priorite } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { dateHeureFr } from '@/lib/utils';
import { OngletsAdministration } from './Onglets';

const GROUPES: GroupeParametre[] = ['SECURITE', 'CIRCUIT', 'PILOTAGE', 'COURRIER', 'NOTIFICATIONS', 'TRESORERIE', 'RAPPORTS', 'RESSOURCES', 'PTBA', 'CONGES', 'PRETS', 'PROCEDURES'];
const CHAMPS_DELAIS: Array<[keyof DelaisCourrier[Priorite], string]> = [
  ['echeance', 'Échéance proposée'],
  ['analyse', 'Analyse du conseiller'],
  ['rappel', 'Rappel avant échéance'],
  ['escalade', 'Escalade après retard'],
];

/** Un paramètre : affichage et saisie selon son type, enregistrement et retour à la valeur par défaut. */
function Parametre({ p, admin }: { p: ParametreApp; admin: boolean }) {
  const actions = useActionsParametres();
  const [valeur, setValeur] = useState<unknown>(p.valeur);
  useEffect(() => setValeur(p.valeur), [p.valeur]);
  const modifie = JSON.stringify(valeur) !== JSON.stringify(p.valeur);

  const enregistrer = async () => {
    try {
      await actions.modifier.mutateAsync({ cle: p.cle, valeur });
      toast.success(`${p.libelle} : enregistré.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible.');
    }
  };
  const reinitialiser = async () => {
    try {
      await actions.reinitialiser.mutateAsync(p.cle);
      toast.success(`${p.libelle} : valeur par défaut rétablie.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Action impossible.');
    }
  };

  let champ: React.ReactNode;
  switch (p.type) {
    case 'nombre':
      champ = <Input type="number" className="num w-40" min={p.min} max={p.max} value={Number(valeur)} onChange={(e) => setValeur(Number(e.target.value))} disabled={!admin} />;
      break;
    case 'texte':
      champ = <Input className="w-60" maxLength={p.max} value={String(valeur ?? '')} onChange={(e) => setValeur(e.target.value.trim().toUpperCase())} disabled={!admin} />;
      break;
    case 'booleen':
      champ = (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={Boolean(valeur)} onCheckedChange={(c) => setValeur(c === true)} disabled={!admin} /> {valeur ? 'Oui' : 'Non'}
        </label>
      );
      break;
    case 'liste':
      champ = <Textarea rows={Math.min(8, Math.max(3, (valeur as string[]).length + 1))} value={(valeur as string[]).join('\n')} onChange={(e) => setValeur(e.target.value.split('\n').map((x) => x.trim()).filter(Boolean))} disabled={!admin} placeholder="Une instruction par ligne" />;
      break;
    case 'delais': {
      const d = valeur as DelaisCourrier;
      champ = (
        <table className="text-sm">
          <thead>
            <tr>
              <th className="pr-3 text-left font-medium">Priorité</th>
              {CHAMPS_DELAIS.map(([k, l]) => (
                <th key={k} className="px-2 text-right font-medium">{l}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(Object.keys(LIBELLE_PRIORITE) as Priorite[]).map((pr) => (
              <tr key={pr}>
                <td className="pr-3">{LIBELLE_PRIORITE[pr]}</td>
                {CHAMPS_DELAIS.map(([k]) => (
                  <td key={k} className="px-2 py-1 text-right">
                    <Input type="number" min={0} max={365} className="num h-8 w-20 text-right" value={d[pr]?.[k] ?? 0} onChange={(e) => setValeur({ ...d, [pr]: { ...d[pr], [k]: Number(e.target.value) } })} disabled={!admin} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
      break;
    }
  }

  return (
    <div className="space-y-2 border-t py-4 first:border-t-0 first:pt-0">
      <div>
        <div className="font-medium">
          {p.libelle} {p.parDefaut ? <Badge variant="outline">valeur par défaut</Badge> : <Badge variant="secondary">administré</Badge>}
        </div>
        <div className="text-xs text-muted-foreground">{p.description}</div>
        {!p.parDefaut && p.modifiePar && <div className="text-xs text-muted-foreground">Modifié par {p.modifiePar} le {dateHeureFr(p.modifieLe)}.</div>}
      </div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">{champ}</div>
        {admin && (
          <div className="flex gap-2">
            {!p.parDefaut && (
              <Button size="sm" variant="ghost" onClick={reinitialiser} disabled={actions.reinitialiser.isPending} title="Revenir à la valeur des spécifications">
                <RotateCcw className="size-4" /> Par défaut
              </Button>
            )}
            <Button size="sm" onClick={enregistrer} disabled={!modifie || actions.modifier.isPending}>
              <Save className="size-4" /> Enregistrer
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export function Parametres() {
  const session = useSession();
  const admin = session?.utilisateur.profils.includes('ADMINISTRATEUR') ?? false;
  const { data, isLoading, error } = useParametres();
  return (
    <div>
      <PageHeader titre="Administration" sousTitre="Seuils du circuit de validation, délais, bibliothèque d’instructions, relances : valeurs des spécifications, modifiables par l’administrateur sur autorisation du DG." />
      <OngletsAdministration />
      {isLoading ? (
        <Chargement />
      ) : error || !data ? (
        <ErreurBloc erreur={error} />
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          {GROUPES.map((g) => (
            <Card key={g} className={g === 'COURRIER' ? 'lg:col-span-2' : undefined}>
              <CardHeader>
                <CardTitle>{LIBELLE_GROUPE_PARAMETRE[g]}</CardTitle>
                {g === 'CIRCUIT' && <CardDescription>Les seuils s’appliquent aux documents soumis après la modification ; les circuits déjà ouverts ne changent pas.</CardDescription>}
              </CardHeader>
              <CardContent>
                {data.filter((p) => p.groupe === g).map((p) => (
                  <Parametre key={p.cle} p={p} admin={admin} />
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
